import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AuthShell } from '../components/AuthShell';
import {
  selectClient,
  selectClientOptions,
  type SelectableClient,
} from '../api/auth';
import { ApiError } from '../api/client';

/**
 * /select-client - post-login chooser for users who belong to
 * multiple active clients and don't have a primary set.
 *
 * Server holds the pending login state in session - `pending_user_email`
 * + `pending_remember`. We GET /api/auth/select-client/options
 * which 403s with NO_PENDING_LOGIN if that state is missing
 * (session expired, user navigated here directly, etc.). On a 403
 * we surface the error with a "Sign in again" link rather than
 * auto-redirecting - less jarring, and the user might want to
 * read what happened.
 *
 * The chooser is a stack of radio cards. Whole row is clickable;
 * keyboard navigation works because the underlying radios are
 * focusable (just visually hidden).
 */
export function SelectClient() {
  const navigate = useNavigate();
  const optionsQuery = useQuery({
    queryKey: ['select-client-options'],
    queryFn: selectClientOptions,
    retry: false,
    staleTime: 30_000,
  });

  if (optionsQuery.isLoading) {
    return (
      <AuthShell>
        <p style={{ textAlign: 'center', color: 'var(--text-dim)' }}>
          Loading your organisations…
        </p>
      </AuthShell>
    );
  }

  if (optionsQuery.isError) {
    const err = optionsQuery.error;
    const message =
      err instanceof ApiError
        ? err.message
        : 'Something went wrong. Please try again.';
    return (
      <AuthShell
        title="Can't continue"
        footer={
          <a
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/login');
            }}
          >
            Sign in again
          </a>
        }
      >
        <div className="auth-alert auth-alert-warning" role="alert">
          {message}
        </div>
      </AuthShell>
    );
  }

  const clients = optionsQuery.data?.clients ?? [];
  return <Chooser clients={clients} />;
}

interface ChooserProps {
  clients: SelectableClient[];
}

function Chooser({ clients }: ChooserProps) {
  const firstClientId = clients[0]?.client_id ?? '';
  const [selected, setSelected] = useState<string>(firstClientId);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // If the options list resolves AFTER mount (StrictMode etc.) and
  // selected hasn't been set, sync it. Idempotent - safe.
  useEffect(() => {
    if (!selected && firstClientId) setSelected(firstClientId);
  }, [firstClientId, selected]);

  const mutation = useMutation({
    mutationFn: (clientId: string) => selectClient(clientId),
    onSuccess: (res) => {
      // Full-page nav so the cookie is sent on the next request and
      // /dashboard (still Jinja until PR-D) sees a logged-in user.
      window.location.assign(res.redirect_to);
    },
    onError: (err) => {
      setErrorMessage(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    if (!selected) {
      setErrorMessage('Please choose an organisation.');
      return;
    }
    mutation.mutate(selected);
  }

  return (
    <AuthShell
      title="Choose your organisation"
      subtitle="You belong to more than one Locksmith organisation. Pick one to continue."
      footer={
        <p>
          {/* Server-side logout - the SPA doesn't own the session
              lifecycle yet (that lands with PR-D). */}
          <a href="/logout">Sign out</a>
        </p>
      }
    >
      {errorMessage && (
        <div className="auth-alert auth-alert-danger" role="alert">
          {errorMessage}
        </div>
      )}

      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="org-chooser" role="radiogroup" aria-label="Organisation">
          {clients.map((c) => (
            <label
              key={c.client_id}
              className={
                'org-option' + (selected === c.client_id ? ' is-selected' : '')
              }
            >
              <input
                className="org-option-radio"
                type="radio"
                name="client_id"
                value={c.client_id}
                checked={selected === c.client_id}
                onChange={() => setSelected(c.client_id)}
                disabled={mutation.isPending}
              />
              {c.logo_url ? (
                <img
                  className="org-option-logo"
                  src={c.logo_url}
                  alt=""
                  /* Decorative - name is read out by the row. */
                />
              ) : (
                <div className="org-option-placeholder" aria-hidden>
                  {c.name.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="org-option-info">
                <p className="org-option-name">{c.name}</p>
                <p
                  className={
                    'org-option-role' +
                    (c.role === 'admin' ? ' is-admin' : '')
                  }
                >
                  {c.role}
                </p>
              </div>
            </label>
          ))}
        </div>

        <button
          type="submit"
          className="auth-btn"
          disabled={mutation.isPending || !selected}
        >
          {mutation.isPending ? 'Continuing…' : 'Continue'}
        </button>
      </form>
    </AuthShell>
  );
}
